import { Paper, Stack, Text } from "@mantine/core";
import ContainerBody from "../../containers/ContainerBody";
import {
  useLeadsContainerStore,
  useLeadsStore,
} from "../../../../../stores/LeadsStore";
import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  findProspect,
  getFirstPagePrm,
  getFirstPagePrmShared,
  getFirstPagePrmUser,
  updateProspect,
} from "../../../../../api/PRMAPI";
import { notifications } from "@mantine/notifications";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { parseProspectDatafields } from "../../utils";

const defaultPerPage = 10;

const ContainerMobile = ({ container }) => {
  const lang = useLanguage((s) => s.language);
  const { userId } = useParams();
  const filter = useLeadsStore((s) => s.filter);
  const query = useLeadsStore((s) => s.query);
  const sharedConditions = useLeadsStore((s) => s.sharedConditions);
  const updateContainer = useLeadsContainerStore((s) => s.updateContainer);
  const updateContainerItem = useLeadsContainerStore(
    (s) => s.updateContainerItem
  );
  const mobile_searchAll = useLeadsStore((s) => s.mobile_searchAll);

  const containerFilter = useMemo(() => {
    if (container?.status) {
      if (query) {
        if (mobile_searchAll) {
          return {
            mode: "and",
            values: [
              {
                field_name: "any_datafield",
                type: "contains",
                value: query,
              },
            ],
          };
        }
        return {
          mode: "and",
          values: [
            {
              field_name: "status",
              type: "equals",
              value: container?.status,
            },
            {
              field_name: "custom_status",
              type: "equals",
              value: "null",
            },
            {
              field_name: "any_datafield",
              type: "contains",
              value: query,
            },
          ],
        };
      }
      return {
        mode: "and",
        values: [
          {
            field_name: "status",
            type: "equals",
            value: container?.status,
          },
          {
            field_name: "custom_status",
            type: "equals",
            value: "null",
          },
        ],
      };
    }
    if (query) {
      if (mobile_searchAll) {
        return {
          mode: "and",
          values: [
            {
              field_name: "any_datafield",
              type: "contains",
              value: query,
            },
          ],
        };
      }

      return {
        mode: "and",
        values: [
          {
            field_name: container.id === "to_call" ? "status" : "custom_status",
            type: "equals",
            value: container.id.toString(),
          },
          {
            field_name: "any_datafield",
            type: "contains",
            value: query,
          },
        ],
      };
    }
    return {
      mode: "and",
      values: [
        {
          field_name: container.id === "to_call" ? "status" : "custom_status",
          type: "equals",
          value: container.id.toString(),
        },
      ],
    };
  }, [container.status, container.id, query, mobile_searchAll]);

  const fetchInitial = useCallback(() => {
    updateContainer(container?.id || container?.status, { loaded: false });
    const getFunction = userId
      ? getFirstPagePrmUser
      : sharedConditions
      ? getFirstPagePrmShared
      : getFirstPagePrm;

    let mainFilter = {};

    // Filter
    let cleanedFilter = { ...filter };
    if (cleanedFilter && cleanedFilter?.values) {
      cleanedFilter.values = cleanedFilter.values.map((item) => {
        // Clean opener_step filter because of the workflow_step format
        if (item?.field_name?.includes?.("_step") && item?.value) {
          return {
            ...item,
            value: item.value.split("_")[1],
          };
        }
        return item;
      });
    }
    if (filter?.values?.length > 0) {
      mainFilter = {
        ...containerFilter,
        values: [...containerFilter.values, cleanedFilter],
      };
    } else {
      mainFilter = containerFilter;
    }

    getFunction({
      id: userId,
      per_page: defaultPerPage,
      sort: container?.sort || {
        field_name:
          container?.status === "to_call"
            ? "last_call"
            : "last_reply_or_status_changed_date",
        sort_direction: "desc",
      },
      filter: mainFilter,
      conditions: sharedConditions?.map((x) => {
        return {
          ...x,
          ids_list:
            x?.ids_list?.map((x) => x.id) ||
            x?.sharing_targets?.map((x) => x.id),
        };
      }),
    })
      .then((response) => {
        updateContainer(container?.id || container?.status, {
          loading: false,
          loaded: true,
          nextPageUrl: response.data?.next_page,
          total: response.data.number_of_results,
          items: response.data.results.map((item) =>
            parseProspectDatafields(item)
          ),
        });
      })
      .catch((error) => {
        updateContainer(container?.id || container?.status, {
          loading: false,
          loaded: true,
        });
      });
  }, [
    updateContainer,
    container?.id,
    container?.status,
    container?.sort,
    userId,
    sharedConditions,
    filter,
    containerFilter,
  ]);

  //#region DnD
  const onDragOver = useCallback((event) => {
    // if (container?.status && container?.status !== "to_call") {
    //   return;
    // }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = (event) => {
    let newItem = JSON.parse(event.dataTransfer.getData("application/prm"));
    newItem.new = true;
    let target = event.target.closest(".prm-container");
    if (target) {
      const dropContainer = target.getAttribute("data-id");

      if (
        newItem?.custom_status === container.id ||
        (newItem.status === container.id && !newItem?.custom_status)
      ) {
        return;
      } else {
        let newItems = [...container.items];
        newItems.unshift({
          ...newItem,
          custom_status: container.id,
          status_changed_date: new Date(),
        });
        updateContainer(container.id, {
          total: container.total + 1,
          items: newItems,
          needCacheReset: true,
        });

        updateProspect({
          id: newItem.id,
          custom_status: container.status ? null : container.id,
          status: container.status ? container.status : null,
        })
          .then((response) => {
            findProspect(newItem.id).then((res) => {
              notifications.show({
                message: lang.prm.prospect_moved,
                color: "tertiary.8",
              });
              updateContainerItem(
                container.status ? container.status : parseInt(dropContainer),
                newItem.id,
                {
                  ...parseProspectDatafields(res.data.contact_profile),
                  new: false,
                }
              );
            });
          })
          .catch((err) => {
            newItem.new = false;
            // Remove from this container
            let newItemsForDroppedContainner = [...container.items].filter(
              (x) => x.id !== newItem.id
            );

            updateContainer(container.id, {
              total: container.total,
              items: newItemsForDroppedContainner,
              needCacheReset: true,
            });

            // Redo to the previous container
            const previousContainer = useLeadsContainerStore
              .getState()
              .containers.find(
                (x) => x.id === newItem.custom_status || x.id === newItem.status
              );
            let newItemsPrevious = [...previousContainer.items];
            newItemsPrevious.unshift(newItem);
            updateContainer(previousContainer.id, {
              total: previousContainer.total + 1,
              items: newItemsPrevious,
              needCacheReset: true,
            });
          });
      }

      // To remove the prospect from where it was
      document.getElementById("main-prm").classList.add("dropped");
    }

    document.querySelectorAll(".container-overlay").forEach((element) => {
      element.style.display = "none";
    });
    document.querySelectorAll(".main-overlay").forEach((element) => {
      element.style.background = "none";
    });
  };
  //#endregion

  useEffect(() => {
    if (container.loading) {
      fetchInitial();
    }
  }, [container.loading]);

  return (
    <Paper
      component={Stack}
      h={"100%"}
      w={"100%"}
      pos={"relative"}
      gap={0}
      className="prm-container"
      onDrop={onDrop}
      onDragOver={onDragOver}
      data-id={container.id}
      bg={container?.items?.length > 0 ? "#fff" : undefined}
      px={8}
    >
      <Text ml={"auto"} size="sm" fw={600} mt={8}>
        {container?.total?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") ||
          "-"}{" "}
        {lang.prm.status.prospect}
      </Text>
      <ContainerBody container={container} isMobile />
    </Paper>
  );
};

export default ContainerMobile;
